/**
 * CRM-T.5: Email Sync Settings — Connect Outlook, configure sync, manual trigger
 *
 * Shows sync status, last sync time, matched/synced counts.
 * Connect/disconnect toggle. Manual "Sync now" button.
 * Config: inbox/sent folders, AI summarization toggle.
 * Real apiFetch. CSS vars only.
 */
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { formatDateTime, formatNumber } from "@/lib/i18n";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import {
  Mail, RefreshCw, CheckCircle2, XCircle, Settings,
  Inbox, Send, Sparkles, Unplug, PlugZap,
} from "lucide-react";

interface SyncState {
  id: string;
  provider: string;
  isEnabled: boolean;
  lastSyncAt: string | null;
  totalSynced: number;
  totalMatched: number;
  failureCount: number;
  lastError: string | null;
  lastErrorAt: string | null;
  config: {
    syncInbox?: boolean;
    syncSent?: boolean;
    aiSummarize?: boolean;
  };
}

export default function EmailSyncSettingsPage() {
  const [state, setState] = useState<SyncState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [syncResult, setSyncResult] = useState<{ synced: number; matched: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiFetch<{ data: SyncState | null }>("/v1/email-sync/status");
      setState(res.data);
    } catch { setState(null); }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleConnect = async () => {
    setError(null);
    try {
      // In production, this would first trigger OAuth flow to get an integration ID.
      // For now, prompt to connect via integrations hub first.
      setError("Connect your Microsoft account in Settings → Integrations first, then return here to enable email sync.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Connection failed");
    }
  };

  const handleDisconnect = async () => {
    const confirmed = window.confirm("Disable email sync? Synced activities will remain in your timeline.");
    if (!confirmed) return;
    try {
      await apiFetch("/v1/email-sync/disconnect", { method: "POST" });
      await fetchStatus();
    } catch {
      setError("Failed to disconnect");
    }
  };

  const handleSyncNow = async () => {
    setIsSyncing(true);
    setSyncResult(null);
    setError(null);
    try {
      const res = await apiFetch<{ data: { synced: number; matched: number; errors: number } }>(
        "/v1/email-sync/trigger",
        { method: "POST" },
      );
      setSyncResult({ synced: res.data.synced, matched: res.data.matched });
      await fetchStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sync failed");
    } finally {
      setIsSyncing(false);
    }
  };

  const handleConfigChange = async (key: string, value: boolean) => {
    setIsSaving(true);
    try {
      await apiFetch("/v1/email-sync/config", {
        method: "PATCH",
        body: JSON.stringify({ [key]: value }),
      });
      setState((prev) => prev ? { ...prev, config: { ...prev.config, [key]: value } } : prev);
    } catch {
      setError("Failed to update config");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4 max-w-2xl">
        {[1, 2, 3].map((i) => <SkeletonCard key={i} className="h-20" />)}
      </div>
    );
  }

  const isConnected = state?.isEnabled === true;
  const hasError = (state?.failureCount ?? 0) > 0;

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Mail className="h-5 w-5 text-[var(--color-primary)]" />
          <div>
            <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Email Sync</h1>
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Sync Outlook emails to your CRM contact timelines
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center justify-between rounded-lg border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 px-4 py-2">
          <p className="text-xs text-[var(--color-error)]">{error}</p>
          <button type="button" onClick={() => setError(null)} className="text-xs text-[var(--color-error)] hover:underline">Dismiss</button>
        </div>
      )}

      {/* Connection status */}
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${
              isConnected
                ? hasError ? "bg-[var(--color-warning)]/10" : "bg-[var(--color-success)]/10"
                : "bg-[var(--color-surface)]"
            }`}>
              <Mail className={`h-5 w-5 ${
                isConnected
                  ? hasError ? "text-[var(--color-warning)]" : "text-[var(--color-success)]"
                  : "text-[var(--color-muted-foreground)]"
              }`} />
            </div>
            <div>
              <p className="font-medium text-[var(--color-foreground)]">Microsoft Outlook</p>
              <div className="flex items-center gap-1.5">
                {isConnected ? (
                  <>
                    {hasError ? (
                      <XCircle className="h-3.5 w-3.5 text-[var(--color-warning)]" />
                    ) : (
                      <CheckCircle2 className="h-3.5 w-3.5 text-[var(--color-success)]" />
                    )}
                    <span className={`text-xs ${hasError ? "text-[var(--color-warning)]" : "text-[var(--color-success)]"}`}>
                      {hasError ? `Error: ${state?.lastError}` : "Connected & syncing"}
                    </span>
                  </>
                ) : (
                  <span className="text-xs text-[var(--color-muted-foreground)]">Not connected</span>
                )}
              </div>
            </div>
          </div>
          {isConnected ? (
            <button
              type="button"
              onClick={handleDisconnect}
              className="inline-flex items-center gap-1.5 text-xs text-[var(--color-muted-foreground)] hover:text-[var(--color-error)]"
            >
              <Unplug className="h-3.5 w-3.5" /> Disconnect
            </button>
          ) : (
            <button
              type="button"
              onClick={handleConnect}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
            >
              <PlugZap className="h-4 w-4" /> Connect Outlook
            </button>
          )}
        </div>

        {/* Stats */}
        {isConnected && state && (
          <div className="grid grid-cols-3 gap-4 border-t border-[var(--color-border)] pt-4">
            <div className="stagger-item">
              <p className="text-xs text-[var(--color-muted-foreground)]">Last Sync</p>
              <p className="text-sm font-medium text-[var(--color-foreground)]" style={{ fontVariantNumeric: "tabular-nums" }}>
                {state.lastSyncAt ? formatDateTime(state.lastSyncAt) : "Never"}
              </p>
            </div>
            <div className="stagger-item">
              <p className="text-xs text-[var(--color-muted-foreground)]">Emails Synced</p>
              <p className="text-sm font-medium text-[var(--color-foreground)]" style={{ fontVariantNumeric: "tabular-nums" }}>
                {formatNumber(state.totalSynced)}
              </p>
            </div>
            <div className="stagger-item">
              <p className="text-xs text-[var(--color-muted-foreground)]">Matched to Contacts</p>
              <p className="text-sm font-medium text-[var(--color-foreground)]" style={{ fontVariantNumeric: "tabular-nums" }}>
                {formatNumber(state.totalMatched)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Manual sync */}
      {isConnected && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[var(--color-foreground)]">Manual Sync</p>
              <p className="text-xs text-[var(--color-muted-foreground)]">
                Automatic sync runs every 15 minutes. Use this for an immediate sync.
              </p>
            </div>
            <button
              type="button"
              onClick={handleSyncNow}
              disabled={isSyncing}
              className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)] disabled:opacity-50"
            >
              {isSyncing ? (
                <>
                  <Skeleton className="h-4 w-4 bg-[var(--color-foreground)]/10" />
                  <Skeleton className="h-4 w-16 bg-[var(--color-foreground)]/10" />
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  Sync Now
                </>
              )}
            </button>
          </div>

          {syncResult && (
            <div className="mt-3 rounded-lg bg-[var(--color-success)]/5 border border-[var(--color-success)]/20 px-4 py-2">
              <p className="text-xs text-[var(--color-success)]">
                Synced {syncResult.synced} emails, {syncResult.matched} matched to CRM contacts
              </p>
            </div>
          )}
        </div>
      )}

      {/* Config */}
      {isConnected && state && (
        <div className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Settings className="h-4 w-4 text-[var(--color-muted-foreground)]" />
            <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Sync Configuration</h2>
          </div>

          {[
            { key: "syncInbox", label: "Sync Inbox", desc: "Import received emails", icon: Inbox },
            { key: "syncSent", label: "Sync Sent", desc: "Import sent emails", icon: Send },
            { key: "aiSummarize", label: "AI Thread Summaries", desc: "Generate AI summaries per email thread", icon: Sparkles },
          ].map(({ key, label, desc, icon: Icon }) => (
            <div key={key} className="stagger-item flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <Icon className={`h-4 w-4 ${key === "aiSummarize" ? "text-[var(--color-ai,var(--color-primary))]" : "text-[var(--color-muted-foreground)]"}`} />
                <div>
                  <p className="text-sm text-[var(--color-foreground)]">{label}</p>
                  <p className="text-[10px] text-[var(--color-muted-foreground)]">{desc}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleConfigChange(key, !(state.config as Record<string, boolean>)[key])}
                disabled={isSaving}
                className={`relative w-10 h-5 rounded-full transition-colors ${
                  (state.config as Record<string, boolean>)[key] !== false
                    ? "bg-[var(--color-primary)]"
                    : "bg-[var(--color-border)]"
                }`}
              >
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                  (state.config as Record<string, boolean>)[key] !== false ? "translate-x-5" : "translate-x-0.5"
                }`} />
              </button>
            </div>
          ))}

          {state.config.aiSummarize !== false && (
            <div className="flex items-center gap-2 rounded-lg border border-[var(--color-ai,var(--color-primary))]/20 bg-[var(--color-ai,var(--color-primary))]/5 px-3 py-2">
              <Sparkles className="h-3.5 w-3.5 text-[var(--color-ai,var(--color-primary))] shrink-0" />
              <p className="text-[10px] text-[var(--color-ai,var(--color-primary))]">
                AI summarizes email threads — one summary per conversation, not per message. Email body is NOT stored.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Privacy notice */}
      <div className="rounded-lg bg-[var(--color-surface)] px-4 py-3">
        <p className="text-[10px] text-[var(--color-muted-foreground)]">
          Email sync is read-only — we never send emails on your behalf. Only subject lines and AI summaries are stored.
          Full email bodies are NOT stored in the CRM. Access is logged for audit compliance.
        </p>
      </div>
    </div>
  );
}
